/**
 * /pros — P.R.O.S. (Professionals Reaching Our Scholars) Speaker Booking
 *
 * Dual-panel:
 *   A. "Book a Friday Slot" — Friday-only picker, duration → time slots,
 *      speaker registration form, instant-confirm modal with .ics download.
 *   B. "Live P.R.O.S. Speaker Calendar" — upcoming Fridays with booked
 *      speaker cards (public view; contact info redacted).
 *
 * Light aesthetic (deliberately inverted from the rest of the dark SAA site):
 *   bg #F8F9FA, cards #FFFFFF, ink #0F172A, gold #B45309 accent.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRight, CalendarDays, Check, Clock, Download, ExternalLink,
  Loader2, MapPin, Sparkles, Users, X,
} from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

const C = {
  bg: '#F8F9FA',
  card: '#FFFFFF',
  ink: '#0F172A',
  sub: '#334155',
  dim: '#64748B',
  line: '#E2E8F0',
  grid: '#E5E7EB',
  gold: '#B45309',
  goldSoft: '#FEF3C7',
};
const TAG = 'text-[10px] font-black uppercase tracking-[0.28em]';

// Presentation topic-tag suggestions (speaker picks or types their own)
const TOPIC_TAG_SUGGESTIONS = [
  'Financial Literacy', 'Sports Law & NIL', 'Tech & AI', 'Real Estate',
  'Entrepreneurship', 'Media & Broadcasting', 'Health & Nutrition', 'College Recruiting',
];
const CONNECTION_OPTIONS = [
  { v: 'parent', l: 'SAA Parent / Guardian' },
  { v: 'community', l: 'Community Member' },
  { v: 'industry_expert', l: 'Industry Expert' },
  { v: 'sponsor', l: 'Sponsor' },
];

const Input = ({ label, value, onChange, testId, type = 'text', required = false, placeholder }) => (
  <div>
    <label className={TAG + ' text-slate-500 mb-1 block'}>{label}{required && <span className="text-amber-700"> *</span>}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full bg-white border border-slate-300 focus:border-amber-700 focus:ring-1 focus:ring-amber-700 outline-none px-3 py-2 text-sm text-slate-900"
      data-testid={testId}
    />
  </div>
);

const Header = () => (
  <header className="border-b" style={{ borderColor: C.line, background: C.card }}>
    <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: C.goldSoft, border: `1px solid ${C.gold}` }}>
          <Users className="w-5 h-5" style={{ color: C.gold }} />
        </div>
        <div>
          <div className={TAG} style={{ color: C.gold }}>P.R.O.S.</div>
          <div className="text-lg font-black leading-tight" style={{ color: C.ink }}>Professionals Reaching Our Scholars</div>
        </div>
      </div>
      <a
        href="/prep-pod"
        className="hidden md:inline-flex items-center gap-1 text-xs font-black uppercase tracking-widest px-3 py-2 border"
        style={{ borderColor: C.line, color: C.sub }}
      >
        The Prep Pod <ExternalLink className="w-3 h-3" />
      </a>
    </div>
  </header>
);

const Hero = () => (
  <section className="py-10 md:py-14" style={{ background: `radial-gradient(600px at 50% 0%, ${C.goldSoft}, ${C.bg})` }}>
    <div className="max-w-4xl mx-auto px-6 text-center">
      <div className={TAG + ' mb-3'} style={{ color: C.gold }}>Friday Masterclass Series</div>
      <h1 className="text-4xl md:text-5xl lg:text-6xl font-black leading-[0.95] tracking-tight mb-4" style={{ color: C.ink, fontFamily: '"Oswald", "Barlow Condensed", Inter, sans-serif' }}>
        Pass down the <span style={{ color: C.gold }}>blueprint.</span>
      </h1>
      <p className="text-base md:text-lg max-w-2xl mx-auto" style={{ color: C.sub, lineHeight: 1.6 }}>
        Connect with the next generation of elite scholar-athletes. Book a 30, 60, or 90-minute Friday session and share what you wish someone had told you at their age.
      </p>
    </div>
  </section>
);

// ─── PANEL A ─────────────────────────────────────────────────────────────
const BookingPanel = ({ availability, refresh }) => {
  const [duration, setDuration] = useState(60);
  const [pickedDate, setPickedDate] = useState(null);
  const [pickedTime, setPickedTime] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [form, setForm] = useState({
    speaker_name: '', speaker_email: '', speaker_phone: '',
    company_title: '', connection_type: 'industry_expert',
    presentation_topic: '', topic_tag: '', headshot_url: '', website: '',
  });
  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  // Reset slot when duration changes.
  useEffect(() => { setPickedDate(null); setPickedTime(null); }, [duration]);

  const fridays = availability?.fridays || [];
  const chosenFriday = fridays.find((f) => f.date === pickedDate);
  const availableTimes = chosenFriday ? (chosenFriday.slots_by_duration[String(duration)] || []) : [];

  const submit = async () => {
    if (!pickedDate || !pickedTime) return toast.error('Pick a Friday and a time.');
    if (!form.speaker_name || !form.speaker_email || !form.speaker_phone || !form.company_title || !form.presentation_topic) {
      return toast.error('Fill the required fields (marked with *).');
    }
    setSubmitting(true);
    try {
      const r = await api.post('/pros/book', {
        ...form,
        speaker_email: form.speaker_email.trim().toLowerCase(),
        booking_date: pickedDate,
        time_slot_24h: pickedTime,
        duration_min: duration,
      });
      setConfirmed(r.data.booking);
      toast.success('Slot locked & confirmed.');
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Booking failed — try another slot.');
    } finally {
      setSubmitting(false);
    }
  };

  const downloadIcs = () => {
    if (!confirmed) return;
    window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/pros/booking/${confirmed.id}/ics`;
  };

  const bookAnother = () => {
    setConfirmed(null);
    setPickedDate(null); setPickedTime(null);
    setForm({ speaker_name: '', speaker_email: '', speaker_phone: '', company_title: '', connection_type: 'industry_expert', presentation_topic: '', topic_tag: '', headshot_url: '', website: '' });
  };

  return (
    <div className="bg-white border p-6 md:p-8 shadow-sm" style={{ borderColor: C.line }} data-testid="pros-book-panel">
      <div className={TAG + ' mb-2'} style={{ color: C.gold }}>Panel A · Book a Friday Slot</div>
      <h2 className="text-2xl md:text-3xl font-black tracking-tight mb-6" style={{ color: C.ink }}>Lock your Friday.</h2>

      {/* Step 1: duration */}
      <div className="mb-5">
        <div className={TAG + ' text-slate-500 mb-2'}>1 · How long do you want to speak?</div>
        <div className="grid grid-cols-3 gap-2 max-w-md">
          {[30, 60, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDuration(d)}
              className="py-3 border-2 text-sm font-black tracking-widest uppercase transition-all"
              style={{
                background: duration === d ? C.gold : C.card,
                color: duration === d ? '#fff' : C.ink,
                borderColor: duration === d ? C.gold : C.line,
              }}
              data-testid={`pros-duration-${d}`}
            >
              {d} min
            </button>
          ))}
        </div>
      </div>

      {/* Step 2: Friday picker */}
      <div className="mb-5">
        <div className={TAG + ' text-slate-500 mb-2'}>2 · Pick your Friday</div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 max-h-72 overflow-y-auto pr-1">
          {fridays.map((f) => {
            const hasSlots = (f.slots_by_duration[String(duration)] || []).length > 0;
            const selected = pickedDate === f.date;
            return (
              <button
                key={f.date}
                disabled={!hasSlots}
                onClick={() => { setPickedDate(f.date); setPickedTime(null); }}
                className="p-3 border-2 text-left transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                style={{
                  background: selected ? C.gold : C.card,
                  color: selected ? '#fff' : C.ink,
                  borderColor: selected ? C.gold : (hasSlots ? C.line : C.grid),
                }}
                data-testid={`pros-friday-${f.date}`}
              >
                <div className={TAG} style={{ color: selected ? '#FEF3C7' : C.gold }}>Friday</div>
                <div className="text-sm font-black leading-tight mt-1">
                  {new Date(f.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </div>
                <div className="text-[10px] mt-1" style={{ color: selected ? '#FEF3C7' : C.dim }}>
                  {hasSlots ? `${(f.slots_by_duration[String(duration)] || []).length} slot${(f.slots_by_duration[String(duration)] || []).length === 1 ? '' : 's'} open` : 'Full'}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Step 3: time */}
      {pickedDate && (
        <div className="mb-5">
          <div className={TAG + ' text-slate-500 mb-2'}>3 · Pick your start time</div>
          <div className="flex flex-wrap gap-2">
            {availableTimes.length === 0 && (
              <div className="text-xs italic" style={{ color: C.dim }}>
                All {duration}-min windows are booked for this Friday. Pick another Friday or a shorter duration.
              </div>
            )}
            {availableTimes.map((t) => (
              <button
                key={t.time_24h}
                onClick={() => setPickedTime(t.time_24h)}
                className="px-4 py-2 border-2 text-sm font-black tracking-widest uppercase"
                style={{
                  background: pickedTime === t.time_24h ? C.gold : C.card,
                  color: pickedTime === t.time_24h ? '#fff' : C.ink,
                  borderColor: pickedTime === t.time_24h ? C.gold : C.line,
                }}
                data-testid={`pros-time-${t.time_24h}`}
              >
                {t.time_display}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Step 4: form */}
      {pickedDate && pickedTime && (
        <div className="pt-4 border-t space-y-4" style={{ borderColor: C.line }}>
          <div className={TAG + ' text-slate-500'}>4 · Tell us about you</div>
          <div className="grid md:grid-cols-2 gap-3">
            <Input label="Full Name" required value={form.speaker_name} onChange={(v) => setF('speaker_name', v)} testId="pros-form-name" />
            <Input label="Email" type="email" required value={form.speaker_email} onChange={(v) => setF('speaker_email', v)} testId="pros-form-email" />
            <Input label="Phone" type="tel" required value={form.speaker_phone} onChange={(v) => setF('speaker_phone', v)} testId="pros-form-phone" />
            <Input label="Title / Company" required placeholder="Founder @ TechX · Sports Attorney · Real Estate Investor" value={form.company_title} onChange={(v) => setF('company_title', v)} testId="pros-form-title" />
          </div>
          <div>
            <div className={TAG + ' text-slate-500 mb-1'}>Academy Connection <span className="text-amber-700">*</span></div>
            <select
              value={form.connection_type}
              onChange={(e) => setF('connection_type', e.target.value)}
              className="w-full bg-white border border-slate-300 focus:border-amber-700 outline-none px-3 py-2 text-sm text-slate-900"
              data-testid="pros-form-connection"
            >
              {CONNECTION_OPTIONS.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
            </select>
          </div>
          <div>
            <div className={TAG + ' text-slate-500 mb-1'}>Presentation Topic / Key Takeaway <span className="text-amber-700">*</span></div>
            <textarea
              value={form.presentation_topic}
              onChange={(e) => setF('presentation_topic', e.target.value.slice(0, 1200))}
              rows={3}
              placeholder="In 2–3 sentences: what will you share with our scholars?"
              className="w-full bg-white border border-slate-300 focus:border-amber-700 outline-none px-3 py-2 text-sm text-slate-900"
              data-testid="pros-form-topic"
            />
          </div>
          <div>
            <div className={TAG + ' text-slate-500 mb-1'}>Topic Tag (optional)</div>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {TOPIC_TAG_SUGGESTIONS.map((tag) => (
                <button
                  key={tag}
                  onClick={() => setF('topic_tag', tag)}
                  className="px-2 py-1 text-[10px] font-black tracking-widest uppercase border"
                  style={{
                    background: form.topic_tag === tag ? C.gold : C.card,
                    color: form.topic_tag === tag ? '#fff' : C.sub,
                    borderColor: form.topic_tag === tag ? C.gold : C.line,
                  }}
                  data-testid={`pros-tag-${tag.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  {tag}
                </button>
              ))}
            </div>
            <Input label="" placeholder="Or type your own..." value={form.topic_tag} onChange={(v) => setF('topic_tag', v)} testId="pros-form-tag-custom" />
          </div>
          <Input label="Headshot URL or LinkedIn Profile" placeholder="https://linkedin.com/in/... (optional)" value={form.headshot_url} onChange={(v) => setF('headshot_url', v)} testId="pros-form-headshot" />
          <input type="text" value={form.website} onChange={(e) => setF('website', e.target.value)} tabIndex={-1} autoComplete="off" className="hidden" />

          <button
            onClick={submit}
            disabled={submitting}
            className="w-full py-4 text-xs font-black uppercase tracking-[0.22em] flex items-center justify-center gap-2 disabled:opacity-60"
            style={{ background: C.gold, color: '#fff' }}
            data-testid="pros-form-submit"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {submitting ? 'Locking your slot...' : 'Lock My Friday Slot'}
          </button>
        </div>
      )}

      {/* Confirmation modal */}
      <AnimatePresence>
        {confirmed && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: 'rgba(15,23,42,0.65)' }}
            onClick={bookAnother}
            data-testid="pros-confirmed-modal"
          >
            <motion.div
              initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg bg-white p-6 md:p-8 border-2 shadow-2xl"
              style={{ borderColor: C.gold }}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full" style={{ background: C.goldSoft }}>
                  <Check className="w-7 h-7" style={{ color: C.gold }} strokeWidth={3} />
                </div>
                <button onClick={bookAnother} data-testid="pros-confirmed-close"><X className="w-5 h-5 text-slate-500" /></button>
              </div>
              <div className={TAG + ' mb-2'} style={{ color: C.gold }}>Slot Locked & Confirmed</div>
              <h3 className="text-2xl font-black mb-3 leading-tight" style={{ color: C.ink }}>
                See you Friday, {confirmed.speaker_name.split(' ')[0]}.
              </h3>
              <div className="p-4 border-l-4 mb-4" style={{ background: C.goldSoft, borderColor: C.gold }}>
                <div className="text-sm font-bold" style={{ color: C.ink }}>
                  {new Date(confirmed.booking_date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                </div>
                <div className="text-sm" style={{ color: C.sub }}>
                  {confirmed.time_display} · {confirmed.duration_min} min
                </div>
              </div>
              <p className="text-sm mb-5" style={{ color: C.sub, lineHeight: 1.6 }}>
                A confirmation email with a calendar attachment is already on its way to <b>{confirmed.speaker_email}</b>.
                You can also download the calendar file right now:
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={downloadIcs}
                  className="inline-flex items-center gap-2 py-3 px-5 text-xs font-black uppercase tracking-[0.22em]"
                  style={{ background: C.ink, color: '#fff' }}
                  data-testid="pros-confirmed-download-ics"
                >
                  <Download className="w-4 h-4" /> Add to Calendar (.ics)
                </button>
                <button
                  onClick={bookAnother}
                  className="inline-flex items-center gap-2 py-3 px-5 text-xs font-black uppercase tracking-[0.22em] border-2"
                  style={{ borderColor: C.line, color: C.ink }}
                  data-testid="pros-confirmed-book-another"
                >
                  Book Another <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ─── PANEL B ─────────────────────────────────────────────────────────────
const CalendarPanel = ({ calendar }) => {
  const bookings = calendar?.bookings || [];
  // Group by date
  const byDate = useMemo(() => {
    const m = {};
    bookings.forEach((b) => { (m[b.booking_date] ||= []).push(b); });
    return m;
  }, [bookings]);
  const dates = Object.keys(byDate).sort();

  return (
    <div className="bg-white border p-6 md:p-8 shadow-sm" style={{ borderColor: C.line }} data-testid="pros-calendar-panel">
      <div className={TAG + ' mb-2'} style={{ color: C.gold }}>Panel B · Live P.R.O.S. Calendar</div>
      <h2 className="text-2xl md:text-3xl font-black tracking-tight mb-6" style={{ color: C.ink }}>Who's on this Friday.</h2>

      {dates.length === 0 && (
        <div className="p-8 text-center border-2 border-dashed" style={{ borderColor: C.grid }}>
          <CalendarDays className="w-8 h-8 mx-auto mb-3" style={{ color: C.dim }} />
          <div className="font-black text-lg" style={{ color: C.ink }}>Fridays are wide open.</div>
          <div className="text-sm mt-1" style={{ color: C.dim }}>Be the first professional to book a Masterclass slot for our scholars.</div>
        </div>
      )}

      <div className="space-y-5">
        {dates.map((d) => (
          <div key={d}>
            <div className="flex items-baseline gap-3 mb-2">
              <div className="text-lg font-black" style={{ color: C.ink }}>
                {new Date(d + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
              </div>
              <div className={TAG} style={{ color: C.dim }}>{byDate[d].length} confirmed</div>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              {byDate[d].map((b) => (
                <div key={b.id} className="p-4 border" style={{ borderColor: C.line, background: '#FCFCFD' }} data-testid={`pros-calendar-card-${b.id}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-1.5 text-xs font-bold" style={{ color: C.sub }}>
                      <Clock className="w-3.5 h-3.5" style={{ color: C.gold }} />
                      {b.time_display} · {b.duration_min} min
                    </div>
                    <span className="px-2 py-0.5 text-[9px] font-black tracking-widest uppercase" style={{ background: C.goldSoft, color: C.gold, border: `1px solid ${C.gold}` }}>
                      Confirmed
                    </span>
                  </div>
                  <div className="font-black text-base mb-0.5" style={{ color: C.ink }}>{b.speaker_name}</div>
                  <div className="text-xs mb-2" style={{ color: C.dim }}>{b.company_title}</div>
                  <div className="text-sm leading-snug" style={{ color: C.sub }}>{b.presentation_topic}</div>
                  {b.topic_tag && (
                    <span className="inline-block mt-3 px-2 py-1 text-[9px] font-black tracking-widest uppercase border" style={{ color: C.gold, borderColor: C.gold }}>
                      {b.topic_tag}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const Footer = () => (
  <footer className="mt-16 py-8 border-t" style={{ borderColor: C.line, background: C.card }}>
    <div className="max-w-6xl mx-auto px-6 text-center">
      <div className={TAG + ' mb-2'} style={{ color: C.gold }}>Scholar Athlete Academy</div>
      <div className="text-sm flex items-center justify-center gap-1.5" style={{ color: C.dim }}>
        <MapPin className="w-3.5 h-3.5" />
        Handlelife HQ · 20611 Prairie St, Chatsworth, CA
      </div>
    </div>
  </footer>
);

export default function Pros() {
  const [availability, setAvailability] = useState(null);
  const [calendar, setCalendar] = useState(null);

  const load = async () => {
    try {
      const [a, c] = await Promise.all([
        api.get('/pros/availability?weeks=12'),
        api.get('/pros/calendar?weeks=12'),
      ]);
      setAvailability(a.data);
      setCalendar(c.data);
    } catch (e) {
      toast.error('Could not load calendar');
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="min-h-screen" style={{ background: C.bg, fontFamily: 'Inter, system-ui, sans-serif' }} data-testid="pros-landing">
      <Header />
      <Hero />
      <main className="max-w-6xl mx-auto px-6 py-10 grid lg:grid-cols-2 gap-6">
        <BookingPanel availability={availability} refresh={load} />
        <CalendarPanel calendar={calendar} />
      </main>
      <Footer />
    </div>
  );
}
